import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { SESSION_CONFIG } from '../../features/auth/constants';
import { storage } from '../../lib/storage';
import {
  AUTH_BASE_URL,
  DEV_MINT_MODE,
  decodeJwtClaims,
  resolveRole,
} from '../../features/auth/authConfig';

import type { Role, UserBorefieldRole } from '../../features/auth/types';

/** @deprecated Use Role from auth/types instead */
export type UserRole = Role;

export interface User {
  id: string;
  email: string;
  name: string;
  /** @deprecated Use borefieldRoles for per-borefield role assignment */
  role: Role;
  borefieldRoles?: UserBorefieldRole[];
  tenantId: string;
  avatarUrl?: string;
  createdAt?: string;
  lastLoginAt?: string;
}

export interface AuthState {
  user: User | null;
  token: string | null;
  refreshToken: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;
  /**
   * Admin Mode flag (RF-7 / PR2-B.5).
   * When true, the API base query injects X-OXBO-Admin-Mode: true, enabling
   * super_admin cross-tenant access. Only super_admin role may activate this.
   * Shown to the user via the AdminModeBanner (red, role="alert").
   */
  adminMode: boolean;
}

export interface LoginCredentials {
  email: string;
  password: string;
}

export interface LoginResponse {
  user: User;
  token: string;
  refreshToken: string;
  expiresAt: string;
}

const initialState: AuthState = {
  user: null,
  token: null,
  refreshToken: null,
  isAuthenticated: false,
  isLoading: false,
  error: null,
  adminMode: false,
};

// DEV bypass — mints a real JWT via the API's /_dev_auth/mint endpoint when
// BIOTK_AUTH_MODE=dev on the backend. Email pattern `<role>.<org_key>@<anything>`
// (matches the seeded test users from scripts/seed_biotk_orgs_users.py).
// E.g. `owner.org_a@test.biotk.example` mints an Org-A owner JWT. The password
// field is accepted but ignored — the dev_issuer mint is JWT-based, not
// password-based.
//
// FRD-OXBO-002: this path is now gated behind DEV_MINT_MODE (authConfig.ts),
// which is ONLY true for a local Vite dev build with VITE_DEV_MINT_MODE=true.
// QA/PROD builds use the real oxbo-auth credential flow below. The mint
// endpoint 404s in any non-dev API anyway, so this is belt-and-suspenders.
const ORG_A_ID = 'aaaaaaaa-0000-4000-a000-000000000000';
const ORG_B_ID = 'bbbbbbbb-0000-4000-b000-000000000000';
const DEV_ROLES = [
  'owner', 'operator', 'designer', 'pm_cm', 'consultant',
  'driller', 'inspector', 'field_clerk', 'admin', 'super_admin',
] as const;

function devUserUuid(orgIndex: 1 | 2, roleIndex: number): string {
  // Matches _user_uuid() in apps/api/tests/conftest.py:88
  const orgHex = orgIndex.toString(16).padStart(8, '0');
  const roleHex = roleIndex.toString(16).padStart(12, '0');
  return `${orgHex}-0001-4000-a000-${roleHex}`;
}

const DEMO_USER: User = {
  id: 'demo-user-001',
  email: 'demo@geothermal.app',
  name: 'Demo User',
  role: 'super_admin',
  tenantId: 'demo-tenant-001',
  createdAt: new Date().toISOString(),
  lastLoginAt: new Date().toISOString(),
};

// Async thunks
export const login = createAsyncThunk<LoginResponse, LoginCredentials>(
  'auth/login',
  async (credentials, { rejectWithValue }) => {
    // DEV bypass — mint a real JWT via /_dev_auth/mint based on the email's
    // <role>.<org_key>@... pattern. See DEV_MINT_MODE comment above.
    if (DEV_MINT_MODE) {
      const localPart = credentials.email.split('@')[0] ?? '';
      const [roleParsed, orgKeyParsed] = localPart.split('.');
      const orgKey = orgKeyParsed === 'org_b' ? 'org_b' : 'org_a';
      const orgId = orgKey === 'org_b' ? ORG_B_ID : ORG_A_ID;
      const orgIndex: 1 | 2 = orgKey === 'org_b' ? 2 : 1;
      const roleIndex = DEV_ROLES.indexOf(roleParsed as (typeof DEV_ROLES)[number]);
      const safeRoleIndex = roleIndex >= 0 ? roleIndex : 4; // 4 = consultant default
      const resolvedRole: (typeof DEV_ROLES)[number] = DEV_ROLES[safeRoleIndex] ?? 'consultant';
      const userId = devUserUuid(orgIndex, safeRoleIndex);

      const apiBase = import.meta.env.VITE_API_URL || '/api';
      let mintRes: Response;
      try {
        mintRes = await fetch(`${apiBase}/_dev_auth/mint`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            org_id: orgId,
            role: resolvedRole,
            email: credentials.email,
            user_id: userId,
          }),
        });
      } catch (e) {
        return rejectWithValue(`Dev mint network error: ${(e as Error).message}`);
      }
      if (!mintRes.ok) {
        return rejectWithValue(`Dev mint failed (${mintRes.status}). Try email like 'owner.org_a@test.biotk.example'.`);
      }
      const mintData = (await mintRes.json()) as { access_token: string; expires_in?: number };

      const devResponse: LoginResponse = {
        user: {
          ...DEMO_USER,
          id: userId,
          email: credentials.email,
          name: `${resolvedRole}@${orgKey}`,
          role: resolvedRole,
          tenantId: orgId,
        },
        token: mintData.access_token,
        refreshToken: mintData.access_token, // dev: same token; no refresh path yet
        expiresAt: new Date(Date.now() + (mintData.expires_in ?? 3600) * 1000).toISOString(),
      };

      storage.setItem(SESSION_CONFIG.TOKEN_STORAGE_KEY, devResponse.token);
      storage.setItem(SESSION_CONFIG.REFRESH_TOKEN_STORAGE_KEY, devResponse.refreshToken);
      storage.setItem(SESSION_CONFIG.USER_STORAGE_KEY, JSON.stringify(devResponse.user));

      return devResponse;
    }

    // Real login — POST credentials to the oxbo-auth service (FRD-OXBO-002 §RF-7).
    // oxbo-auth returns a flat token pair {access_token, refresh_token, ...};
    // there is no user object in the body, so we derive the User from the JWT
    // claims (sub/org_id/org_roles/email). The biotk-api validates the token
    // against oxbo-auth's JWKS on the first API call.
    let response: Response;
    try {
      response = await fetch(`${AUTH_BASE_URL}/v1/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        // credentials:'include' so the env-parent session cookie is set for
        // cross-app SSO within the env (RF-13); the body tokens remain the
        // primary mechanism this SPA uses (localStorage + Authorization header).
        credentials: 'include',
        body: JSON.stringify(credentials),
      });
    } catch {
      return rejectWithValue('Network error. Please try again.');
    }

    if (!response.ok) {
      if (response.status === 401) {
        return rejectWithValue('Invalid email or password.');
      }
      if (response.status === 403) {
        return rejectWithValue('Email not verified. Check your inbox for the verification link.');
      }
      if (response.status === 429) {
        const retryAfter = response.headers.get('Retry-After');
        return rejectWithValue(
          retryAfter
            ? `Too many attempts. Try again in ${retryAfter} seconds.`
            : 'Too many attempts. Please try again later.'
        );
      }
      const errorData = (await response.json().catch(() => ({}))) as { detail?: string };
      return rejectWithValue(errorData.detail ?? 'Login failed');
    }

    const tokens = (await response.json().catch(() => null)) as {
      access_token?: string;
      refresh_token?: string;
      expires_in?: number;
    } | null;

    if (!tokens?.access_token || !tokens.refresh_token) {
      return rejectWithValue('Login succeeded but the server returned no tokens.');
    }

    const claims = decodeJwtClaims(tokens.access_token);
    if (!claims) {
      return rejectWithValue('Login succeeded but the token could not be read.');
    }

    const resolvedRole = resolveRole(claims);
    const loginResponse: LoginResponse = {
      user: {
        id: claims.sub,
        email: claims.email ?? credentials.email,
        name: claims.email ?? credentials.email,
        role: resolvedRole,
        tenantId: claims.org_id ?? '',
        lastLoginAt: new Date().toISOString(),
      },
      token: tokens.access_token,
      refreshToken: tokens.refresh_token,
      expiresAt: new Date(Date.now() + (tokens.expires_in ?? 900) * 1000).toISOString(),
    };

    storage.setItem(SESSION_CONFIG.TOKEN_STORAGE_KEY, loginResponse.token);
    storage.setItem(SESSION_CONFIG.REFRESH_TOKEN_STORAGE_KEY, loginResponse.refreshToken);
    storage.setItem(SESSION_CONFIG.USER_STORAGE_KEY, JSON.stringify(loginResponse.user));

    return loginResponse;
  }
);

export const refreshToken = createAsyncThunk<{ token: string; refreshToken: string }, void>(
  'auth/refreshToken',
  async (_, { getState, rejectWithValue }) => {
    try {
      const state = getState() as { auth: AuthState };
      const currentRefreshToken = state.auth.refreshToken;

      if (!currentRefreshToken) {
        return rejectWithValue('No refresh token available');
      }

      // FRD-OXBO-002 §RF-7: rotating 30-day refresh token. oxbo-auth expects
      // {refresh_token} (snake_case) and returns a fresh access+refresh pair;
      // the old refresh token is invalidated server-side on use.
      const response = await fetch(`${AUTH_BASE_URL}/v1/auth/refresh`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ refresh_token: currentRefreshToken }),
      });

      if (!response.ok) {
        return rejectWithValue('Token refresh failed');
      }

      const data = (await response.json().catch(() => null)) as {
        access_token?: string;
        refresh_token?: string;
      } | null;

      if (!data?.access_token || !data.refresh_token) {
        // Malformed rotation response — treat as a failed refresh so the
        // reauth path logs out rather than persisting "undefined" tokens.
        return rejectWithValue('Token refresh returned an incomplete pair');
      }

      // Persist the rotated pair (RF-7: store the new refresh token too).
      storage.setItem(SESSION_CONFIG.TOKEN_STORAGE_KEY, data.access_token);
      storage.setItem(SESSION_CONFIG.REFRESH_TOKEN_STORAGE_KEY, data.refresh_token);

      return { token: data.access_token, refreshToken: data.refresh_token };
    } catch {
      return rejectWithValue('Network error during token refresh');
    }
  }
);

export const initializeAuth = createAsyncThunk<LoginResponse | null, void>(
  'auth/initialize',
  () => {
    // Try to restore session from localStorage
    const token = storage.getItem(SESSION_CONFIG.TOKEN_STORAGE_KEY);
    const refreshTokenStored = storage.getItem(SESSION_CONFIG.REFRESH_TOKEN_STORAGE_KEY);
    const userJson = storage.getItem(SESSION_CONFIG.USER_STORAGE_KEY);

    if (token && refreshTokenStored && userJson) {
      try {
        const user = JSON.parse(userJson) as User;
        return {
          user,
          token,
          refreshToken: refreshTokenStored,
          expiresAt: '', // Will be validated on next API call
        };
      } catch {
        // Clear invalid data
        storage.removeItem(SESSION_CONFIG.TOKEN_STORAGE_KEY);
        storage.removeItem(SESSION_CONFIG.REFRESH_TOKEN_STORAGE_KEY);
        storage.removeItem(SESSION_CONFIG.USER_STORAGE_KEY);
      }
    }
    return null;
  }
);

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    logout(state) {
      state.user = null;
      state.token = null;
      state.refreshToken = null;
      state.isAuthenticated = false;
      state.error = null;

      // Clear stored tokens
      storage.removeItem(SESSION_CONFIG.TOKEN_STORAGE_KEY);
      storage.removeItem(SESSION_CONFIG.REFRESH_TOKEN_STORAGE_KEY);
      storage.removeItem(SESSION_CONFIG.USER_STORAGE_KEY);
    },
    clearError(state) {
      state.error = null;
    },
    setUser(state, action: PayloadAction<User>) {
      state.user = action.payload;
    },
    tokenRefreshed(state, action: PayloadAction<string>) {
      state.token = action.payload;
      // Update stored token
      storage.setItem(SESSION_CONFIG.TOKEN_STORAGE_KEY, action.payload);
    },
    /**
     * Toggle Admin Mode (RF-7 / PR2-B.5).
     * Only super_admin users may call this; guard is enforced in the UI.
     * When enabled: X-OXBO-Admin-Mode: true is injected on every API request.
     */
    setAdminMode(state, action: PayloadAction<boolean>) {
      state.adminMode = action.payload;
    },
  },
  extraReducers: (builder) => {
    // Login
    builder
      .addCase(login.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(login.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isAuthenticated = true;
        state.user = action.payload.user;
        state.token = action.payload.token;
        state.refreshToken = action.payload.refreshToken;
        state.error = null;
      })
      .addCase(login.rejected, (state, action) => {
        state.isLoading = false;
        state.isAuthenticated = false;
        state.user = null;
        state.token = null;
        state.refreshToken = null;
        state.error = action.payload as string;
      });

    // Refresh token (RF-7: rotating refresh — store the new pair)
    builder
      .addCase(refreshToken.fulfilled, (state, action) => {
        state.token = action.payload.token;
        state.refreshToken = action.payload.refreshToken;
      })
      .addCase(refreshToken.rejected, (state) => {
        // If refresh fails, log the user out
        state.user = null;
        state.token = null;
        state.refreshToken = null;
        state.isAuthenticated = false;
      });

    // Initialize auth from storage
    builder
      .addCase(initializeAuth.fulfilled, (state, action) => {
        if (action.payload) {
          state.isAuthenticated = true;
          state.user = action.payload.user;
          state.token = action.payload.token;
          state.refreshToken = action.payload.refreshToken;
        }
      });
  },
});

export const { logout, clearError, setUser, tokenRefreshed, setAdminMode } = authSlice.actions;

export const authReducer = authSlice.reducer;

// Selectors
export const selectCurrentUser = (state: { auth: AuthState }) => state.auth.user;
export const selectIsAuthenticated = (state: { auth: AuthState }) => state.auth.isAuthenticated;
export const selectAuthToken = (state: { auth: AuthState }) => state.auth.token;
export const selectAuthError = (state: { auth: AuthState }) => state.auth.error;
export const selectAuthLoading = (state: { auth: AuthState }) => state.auth.isLoading;
/** Selector for Admin Mode flag (PR2-B.5 / RF-7). */
export const selectAdminMode = (state: { auth: AuthState }) => state.auth.adminMode;
